import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, fmtPrice, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MapPin, X, Loader2, Compass, Check } from "lucide-react";

const LEVELS = ["region", "prefecture", "commune", "canton", "localite"];
const LEVEL_LABELS = { region: "Région", prefecture: "Préfecture", commune: "Commune", canton: "Canton", localite: "Localité" };
const REGION_COORDS = {
  DAGL: [6.1725, 1.2314], MARITIME: [6.55, 1.55], PLATEAUX: [7.4, 0.95],
  CENTRALE: [8.55, 1.05], KARA: [9.75, 1.15], SAVANES: [10.65, 0.35],
};
const REGION_LABELS = { DAGL: "Grand Lomé", MARITIME: "Maritime", PLATEAUX: "Plateaux", CENTRALE: "Centrale", KARA: "Kara", SAVANES: "Savanes" };
const prettyRegion = (r) => REGION_LABELS[r] || r;
const zoneName = (level, name) => (level === "region" ? prettyRegion(name) : name);

const MiniProduct = ({ product }) => (
  <Link to={`/product/${product.id}`} className="group block" data-testid={`geo-mini-${product.id}`}>
    <div className="aspect-square rounded-lg overflow-hidden bg-stone-100 border border-stone-200 group-hover:border-mango-500 transition-colors">
      <img src={mediaUrl(product.image)} alt={product.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
    </div>
    <p className="text-[11px] font-medium text-stone-800 line-clamp-1 mt-1 leading-tight group-hover:text-emerald-800">{product.name}</p>
    <p className="text-[11px] font-bold text-mango-700">{fmtPrice(product.price)}</p>
  </Link>
);

const MiniSkeleton = () => (
  <div><div className="aspect-square rounded-lg skeleton" /><div className="h-2.5 w-3/4 skeleton rounded mt-1.5" /><div className="h-2.5 w-1/2 skeleton rounded mt-1" /></div>
);

const emptySel = () => ({ region: [], prefecture: [], commune: [], canton: [], localite: [] });

export const GeoShopping = () => {
  const [sel, setSel] = useState(emptySel());
  const [opts, setOpts] = useState({ region: [] });
  const [products, setProducts] = useState(null);
  const [showProducts, setShowProducts] = useState(false);

  const elRef = useRef();
  const mapRef = useRef();
  const layerRef = useRef();

  const loadOptions = (level, s) => {
    const idx = LEVELS.indexOf(level);
    const params = new URLSearchParams({ group: level });
    for (let i = 0; i < idx; i++) {
      const arr = s[LEVELS[i]] || [];
      if (arr.length) params.set(LEVELS[i], arr.join(","));
    }
    api.get(`/geo/stats?${params.toString()}`)
      .then((r) => setOpts((o) => ({ ...o, [level]: r.data.items })))
      .catch(() => setOpts((o) => ({ ...o, [level]: [] })));
  };

  useEffect(() => { loadOptions("region", emptySel()); }, []);

  const toggle = (level, name) => {
    const idx = LEVELS.indexOf(level);
    const cur = sel[level] || [];
    const nextArr = cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name];
    const next = { ...sel, [level]: nextArr };
    for (let i = idx + 1; i < LEVELS.length; i++) next[LEVELS[i]] = [];
    setSel(next);
    const newOpts = { ...opts };
    for (let i = idx + 2; i < LEVELS.length; i++) newOpts[LEVELS[i]] = [];
    if (idx + 1 < LEVELS.length) {
      if (nextArr.length) loadOptions(LEVELS[idx + 1], next);
      else newOpts[LEVELS[idx + 1]] = [];
    }
    setOpts(newOpts);
    setShowProducts(false); setProducts(null);
  };

  const reset = () => { setSel(emptySel()); setOpts((o) => ({ region: o.region || [] })); setShowProducts(false); setProducts(null); };

  const hasSel = LEVELS.some((l) => (sel[l] || []).length);

  const viewProducts = () => {
    const params = new URLSearchParams({ limit: "60" });
    LEVELS.forEach((l) => (sel[l] || []).length && params.set(l, sel[l].join(",")));
    setShowProducts(true); setProducts(null);
    api.get(`/products?${params.toString()}`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  };

  useEffect(() => {
    if (!elRef.current) return;
    if (!mapRef.current) {
      mapRef.current = L.map(elRef.current, { zoomControl: true, attributionControl: false, scrollWheelZoom: false }).setView([8.6, 1.0], 6);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(mapRef.current);
      layerRef.current = L.layerGroup().addTo(mapRef.current);
    }
    const g = layerRef.current; g.clearLayers();
    const regions = opts.region || [];
    const maxC = Math.max(1, ...regions.map((r) => r.count));
    regions.forEach((r) => {
      const coord = REGION_COORDS[r.name]; if (!coord) return;
      const active = (sel.region || []).includes(r.name);
      const size = 30 + Math.round((r.count / maxC) * 28);
      const html = `<div style="width:${size}px;height:${size}px;border-radius:9999px;background:${active ? "#065F46" : "#EA580C"};color:#fff;border:3px solid #fff;box-shadow:0 3px 12px rgba(0,0,0,.35);display:grid;place-items:center;font-weight:800;font-size:12px;cursor:pointer;">${r.count}</div>`;
      const icon = L.divIcon({ className: "geo-marker", html, iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
      const m = L.marker(coord, { icon }).addTo(g);
      m.bindTooltip(`${prettyRegion(r.name)} · ${r.count} produits`, { direction: "top", offset: [0, -size / 2] });
      m.on("click", () => toggle("region", r.name));
    });
    const applyView = () => {
      if (!mapRef.current) return;
      mapRef.current.invalidateSize();
      const picked = sel.region || [];
      if (picked.length === 1 && REGION_COORDS[picked[0]]) mapRef.current.setView(REGION_COORDS[picked[0]], 9);
      else mapRef.current.setView([8.6, 1.0], 6);
    };
    applyView();
    setTimeout(applyView, 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.region, sel.region]);

  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  const shownLevels = LEVELS.filter((l, i) => i === 0 || (sel[LEVELS[i - 1]] || []).length > 0);

  return (
    <section className="w-full px-4 sm:px-6 lg:px-10 mt-6" data-testid="geo-shopping">
      <div className="bg-white border border-stone-200 rounded-2xl shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-800 to-emerald-700 text-white px-6 py-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-mango-600 grid place-items-center shrink-0"><Compass className="w-5 h-5" /></span>
          <div className="flex-1">
            <p className="font-display font-extrabold text-lg leading-none">GeoShopping</p>
            <p className="text-emerald-100 text-xs mt-1">Explorez le marché par zone — sélectionnez plusieurs régions, préfectures, communes…</p>
          </div>
          {hasSel && <button onClick={reset} className="text-emerald-100 hover:text-white text-xs font-semibold underline shrink-0" data-testid="geo-reset">Réinitialiser</button>}
        </div>

        <div className="p-4 sm:p-5">
          <div className="grid lg:grid-cols-2 gap-5">
            {/* Map */}
            <div className="relative">
              <div ref={elRef} data-testid="geo-map" style={{ height: 360 }} className="rounded-2xl overflow-hidden border border-stone-200 z-0 bg-stone-100" />
              <div className="absolute bottom-3 left-3 z-[400] bg-white/95 backdrop-blur px-3 py-1.5 rounded-full shadow border border-stone-200 text-[11px] font-semibold text-stone-600 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-mango-600" />Cliquez une ou plusieurs régions
              </div>
            </div>

            {/* Multi-select levels */}
            <div className="flex flex-col max-h-[360px] overflow-y-auto pr-1 space-y-4">
              {shownLevels.map((level) => {
                const items = opts[level] || [];
                const picked = sel[level] || [];
                return (
                  <div key={level} data-testid={`geo-level-${level}`}>
                    <p className="text-sm font-bold text-stone-700 mb-2">{level === "region" ? "Régions au Togo" : `${LEVEL_LABELS[level]}s`}</p>
                    <div className="flex flex-wrap gap-2">
                      {items.length === 0 && <span className="text-xs text-stone-400">Aucune donnée</span>}
                      {items.map((it) => {
                        const active = picked.includes(it.name);
                        return (
                          <button key={it.name} onClick={() => toggle(level, it.name)} data-testid={`geo-zone-${it.name}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${active ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-stone-700 border-stone-200 hover:border-mango-500 hover:bg-mango-50"}`}>
                            {active && <Check className="w-3.5 h-3.5" />}
                            {zoneName(level, it.name)}
                            <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${active ? "bg-white/20" : "bg-emerald-800 text-white"}`}>{it.count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {hasSel && (
            <Button onClick={viewProducts} className="mt-5 rounded-full bg-mango-600 hover:bg-mango-700" data-testid="geo-view-products">
              <MapPin className="w-4 h-4 mr-2" />Voir les produits des zones sélectionnées
            </Button>
          )}

          {showProducts && (
            <div className="mt-6 pt-6 border-t border-stone-100" data-testid="geo-products">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-display font-extrabold text-lg">Produits des zones sélectionnées</h4>
                <button onClick={() => setShowProducts(false)} className="text-stone-400 hover:text-stone-700" data-testid="geo-products-close"><X className="w-5 h-5" /></button>
              </div>
              {products === null ? (
                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-3">{Array.from({ length: 8 }).map((_, i) => <MiniSkeleton key={i} />)}</div>
              ) : products.length === 0 ? (
                <p className="text-stone-400 text-sm py-8 text-center flex items-center justify-center gap-2"><Loader2 className="w-4 h-4" />Aucun produit trouvé dans ces zones</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-3">{products.map((p) => <MiniProduct key={p.id} product={p} />)}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
